import { hexToRgba, Rgba2hex } from "../../../utils/helper";
import { CommonUtils } from "./CommonUtils";
import interact from "interactjs";

const transparentColor = "#00000000";

export class CustomTextBoxClass {
  static OnMoveTextBox = ({
    coordinates,
    connectedLeaderLine,
    mapDraw,
    textAreaElement,
    map,
    textBoxDefaultStyle,
    isUpdate,
    allCoordinates,
  }) => {
    const lineConnected = CommonUtils.HideOrShowLeaderLine({
      mapDraw,
      connectedLeaderLine,
      correspondingElement: textAreaElement,
      leaderLine: {
        leaderLineColor: textBoxDefaultStyle.leaderLineColor,
        leaderLineWidth: textBoxDefaultStyle.leaderLineWidth,
      },
      condition: !textAreaElement.dataset.leaderLineEnabled,
    });
    let changedCoordinates;
    if (!isUpdate) {
      changedCoordinates = [...lineConnected.geometry.coordinates];
      changedCoordinates.pop();
      changedCoordinates.push([coordinates.lng, coordinates.lat]);
      lineConnected.geometry.coordinates = changedCoordinates;
      textAreaElement.dataset.leaderLine = JSON.stringify(lineConnected);
    } else {
      lineConnected.geometry.coordinates = allCoordinates;
      changedCoordinates = lineConnected;
    }

    if (JSON.parse(textAreaElement.dataset.leaderLineEnabled)) {
      const source = map.getSource(connectedLeaderLine + "-end-point");
      if (!source) {
        const icon = {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: lineConnected.geometry.coordinates[0],
          },
        };
        map.addSource(connectedLeaderLine + "-end-point", {
          type: "geojson",
          data: icon,
        });
        map.addLayer({
          id: connectedLeaderLine + "-end-point",
          type: "circle",
          source: connectedLeaderLine + "-end-point",
          paint: {
            "circle-radius": 6,
            "circle-color": lineConnected.properties["line-color"],
          },
        });
      }
      mapDraw.setFeatureProperty(
        connectedLeaderLine,
        "coordinates",
        changedCoordinates
      );
      mapDraw.add(lineConnected);
    }

    textAreaElement.dataset.leaderLine = JSON.stringify(lineConnected);
  };

  static GetStyleFromTextBox = ({
    inputMarkerId,
    connectedLeaderLine,
    setTextBox,
    mapDraw,
  }) => {
    const getHexFromTextShadow = (textShadow, data) => {
      // Regular expression to find the rgba or rgb part of the text-shadow string
      const match = textShadow.match(/(?:rgba?\([^)]*\)|[^\s]+)\s*/g);
      if (!match) {
        return ""; // or throw an error, or return a default value
      }
      if (data === "color") {
        return Rgba2hex(match[0]);
      } else {
        return data === "x"
          ? match[1].split("px")[0]
          : data === "y"
          ? match[2].split("px")[0]
          : match[3].split("px")[0];
      }
    };
    mapDraw = mapDraw.current || mapDraw;
    const textarea = document.getElementById(inputMarkerId);
    const lineConnected = mapDraw.get(connectedLeaderLine);

    // Check if this is a radius ring text box
    const isRadiusRing = textarea.dataset.currentSetting === "ringRadius";

    const haloEffectCode = textarea.style.textShadow || "";
    const boxShadowArray = haloEffectCode.split(/,(?![^()]*\))/);
    const firstValue = boxShadowArray[0];
    const lastValue = boxShadowArray[boxShadowArray.length - 1];

    let haloEffect = "";
    let dropShadow = "";
    const halox = "";
    const haloy = "";
    const halob = "";
    const dropx = "";
    const dropy = "";
    const dropb = "";
    //TODO: Uncomment when added halo and drop shadow effect.

    // Fix: Add proper validation for text shadow values
    try {
      if (firstValue && firstValue.trim() !== "") {
        haloEffect = getHexFromTextShadow(firstValue, "color");
      }
    } catch (error) {
      haloEffect = "";
    }

    try {
      if (lastValue && lastValue.trim() !== "") {
        dropShadow = getHexFromTextShadow(lastValue, "color");
      }
    } catch (error) {
      dropShadow = "";
    }

    const rotate = textarea.dataset.rotate || "0";
    const backgroundColor = textarea.dataset.backgroundColor || "#808080";

    // Fix: Add proper validation for borderColor before calling Rgba2hex
    let borderColor;
    try {
      borderColor =
        textarea.style.borderColor && textarea.style.borderColor.trim() !== ""
          ? Rgba2hex(textarea.style.borderColor)
          : transparentColor;
    } catch (error) {
      // If Rgba2hex fails, use the dataset value or default
      borderColor = textarea.dataset.borderColor || "#b5ef9d";
    }

    const isBackgroundColorTransparent = JSON.parse(
      textarea.dataset.isTransparent || "false"
    );
    let isBorderColorTransparent = true;
    let isHaloEffectTransparent = true;
    let isDropShadowTransparent = true;

    if (borderColor === transparentColor) {
      borderColor = textarea.dataset.borderColor || "#b5ef9d";
      isBorderColorTransparent = false;
    }

    if (haloEffect === transparentColor) {
      haloEffect = textarea.dataset.textShadow || "";
      isHaloEffectTransparent = false;
    }
    if (dropShadow === transparentColor) {
      dropShadow = textarea.dataset.dropShadow || "";
      isDropShadowTransparent = false;
    }
    let fontStyle = "none";

    if (
      textarea.style.fontStyle === "italic" &&
      textarea.style.fontWeight === "bold"
    ) {
      fontStyle = "boldItalic";
    } else if (textarea.style.fontStyle === "italic") {
      fontStyle = "italic";
    } else if (textarea.style.fontWeight === "bold") {
      fontStyle = "bold";
    }

    // For radius ring text boxes, ensure we have proper default values
    const fontSize = textarea.style.fontSize
      ? parseInt(textarea.style.fontSize)
      : isRadiusRing
      ? 15
      : 10;
    const fontColor =
      textarea.style.color || (isRadiusRing ? "#000000" : "#00000080");
    const fontFamily = textarea.style.fontFamily
      ? textarea.style.fontFamily.replace(/\"/g, "")
      : isRadiusRing
      ? "Arial"
      : "Arial";
    const textAlign =
      textarea.style.textAlign || (isRadiusRing ? "left" : "left");
    const borderWidth =
      parseInt(textarea.style.borderWidth) || (isRadiusRing ? 2 : 2);
    const opacity =
      parseFloat(textarea.dataset.opacity) || (isRadiusRing ? 1 : 0.5);
    const width = textarea.style.width
      ? +textarea.style.width.split("px")[0]
      : isRadiusRing
      ? 200
      : 200;
    const height = textarea.style.height
      ? +textarea.style.height.split("px")[0]
      : isRadiusRing
      ? 75
      : 75;

    setTextBox((previousValue) => {
      const selectedTextBox = {
        style: {
          font: {
            color: fontColor,
            fontFamily: fontFamily,
            size: fontSize,
            style: fontStyle,
            alignment: textAlign,
          },
          textBox: {
            fillColor: backgroundColor,
            isFillTransparent: isBackgroundColorTransparent,
            borderColor: borderColor,
            borderWidth: borderWidth,
            isBorderTransparent: isBorderColorTransparent,
            opacity: opacity,
            haloEffect: haloEffect,
            haloEffect_x: +halox,
            haloEffect_y: +haloy,
            haloEffect_b: +halob,
            haloEffect_c: +halob,
            isHaloEffectTransparent: isHaloEffectTransparent,
            dropShadow: dropShadow,
            dropShadow_x: +dropx,
            dropShadow_y: +dropy,
            dropShadow_b: +dropb,
            dropShadow_c: +dropb,
            isDropShadowTransparent: isDropShadowTransparent,
            currentSetting: String(textarea.dataset.currentSetting),
            width: width,
            height: height,
            content: textarea.value,
            lat: String(textarea.dataset.lat),
            lng: String(textarea.dataset.lng),
            rotate: Number(rotate),
          },
          leaderLine: {
            color: lineConnected
              ? lineConnected.properties["line-color"]
              : textarea.dataset.connectedLeaderLineColor,
            width: lineConnected
              ? lineConnected.properties["line-width"]
              : textarea.dataset.connectedLeaderLineWidth,
            isLineTransparent: JSON.parse(textarea.dataset.leaderLineEnabled),
            coordinates: JSON.parse(textarea.dataset.leaderLine).geometry[
              "coordinates"
            ],
          },
        },
        id: inputMarkerId,
        leaderLine: connectedLeaderLine,
        isResetStyle: true,
      };
      return { ...previousValue, selectedTextBox };
    });
  };

  static updatePreviousValueChange = ({
    action,
    setDrawnFeaturesSupport,
    inputMarkerId,
    textarea,
    setTextBox,
    // setUndoHistory,
    // setRedoHistory,
    isEnabledAction = "textSetting",
  }) => {
    setDrawnFeaturesSupport((previousValue) => {
      const preFeature = previousValue[inputMarkerId];
      previousValue[inputMarkerId] =
        action === "deleted" ? null : textarea.cloneNode();
      const updatedText = {
        action: action,
        type: "text",
        id: inputMarkerId,
        previousValue: action === "created" ? null : preFeature,
        currentValue: action === "deleted" ? null : textarea.cloneNode(),
      };
      if (action === "updated" || action === "deleted") {
        setTextBox((previousValue) => {
          if (previousValue && previousValue.selectedTextBox) {
            previousValue.selectedTextBox.style.textBox.width = parseInt(
              textarea.style.width
            );
            previousValue.selectedTextBox.style.textBox.height = parseInt(
              textarea.style.height
            );
            previousValue.selectedTextBox.style.textBox.content =
              textarea.value;
          }
          return previousValue;
        });
      }
      // if (action !== "created" || isEnabledAction !== "radiusSetting") {
      //   setUndoHistory((previousValue) => {
      //     let newUndoHistory = [...previousValue];
      //     if (newUndoHistory && newUndoHistory.length >= 5) {
      //       newUndoHistory.shift();
      //     }
      //     newUndoHistory.push(updatedText);
      //     return newUndoHistory;
      //   });
      // }
      // setRedoHistory([]);
      return previousValue;
    });
  };

  static CreateTextBox = async ({
    coordinates,
    mapboxgl,
    map,
    mapDraw,
    resetAllOnceChanged,
    setTextBox,
    setActiveId,
    textBoxDefaultStyle,
    lastClickButton,
    // updateAutoSave,
    currentMode,
    polygonDefaultStyle,
    isEnabledAction,
    disableUserActions,
    defaultText,
    currentMarkerId,
    // setUndoHistory,
    // setRedoHistory,
    setDrawnFeaturesSupport,
    isCreate,
    previousValue,
    markerId,
    markerRef,
  }) => {
    // Create the outer div element
    const outerDiv = document.createElement("div");

    // Create the textarea element
    let textarea;
    let inputMarkerId;
    if (isCreate) {
      inputMarkerId =
        currentMarkerId !== ""
          ? currentMarkerId
          : "textArea-" +
            String(coordinates.lng) +
            "-" +
            String(coordinates.lat);
      textarea = document.createElement("textarea");
      textarea.setAttribute("data-enable-grammarly", "false");
      textarea.setAttribute("spellcheck", "false");
      textarea.classList.add("map-text-box-feature");
      textarea.setAttribute("type", "text");
      textarea.setAttribute("data-gramm-editor", "false");
      // textarea.style.maxWidth = "500px";
      // textarea.style.maxHeight = "500px";
      // textarea.style.minWidth = "50px";
      // textarea.style.minHeight = "50px";
      textarea.style.lineHeight = "normal";
      textarea.style.width = textBoxDefaultStyle.width;
      textarea.style.height = textBoxDefaultStyle.height;
      textarea.style.resize = "both";
      textarea.style.caretColor = "#000";
      textarea.id = inputMarkerId;
      textarea.style.transform = `rotate(${textBoxDefaultStyle.rotate}deg)`;
      textarea.dataset.textShadow = textBoxDefaultStyle.textBoxHaloEffect;
      textarea.dataset.dropShadow = textBoxDefaultStyle.textBoxDropShadow;
      textarea.dataset.borderColor = textBoxDefaultStyle.textBoxBorderColor;
      textarea.dataset.backgroundColor = textBoxDefaultStyle.textBoxFillColor;
      textarea.dataset.rotate = textBoxDefaultStyle.rotate;
      textarea.dataset.lng = coordinates.lng;
      textarea.dataset.lat = coordinates.lat;
      if (isEnabledAction === "textSetting") {
        textarea.style.fontSize =
          String(textBoxDefaultStyle.textBoxFontSize) + "px";
        textarea.style.fontStyle =
          textBoxDefaultStyle.textBoxFontSize &&
          ["italic", "boldItalic"].includes(
            textBoxDefaultStyle.textBoxFontStyle
          )
            ? "italic"
            : "";
        textarea.style.fontWeight =
          textBoxDefaultStyle.textBoxFontSize &&
          ["bold", "boldItalic"].includes(textBoxDefaultStyle.textBoxFontStyle)
            ? "bold"
            : "";
        textarea.style.color = textBoxDefaultStyle.textBoxFontColor;
        textarea.style.fontFamily = textBoxDefaultStyle.textBoxFontFamily;
        textarea.style.textAlign = textBoxDefaultStyle.textBoxFontAlignment;
        textarea.dataset.isTransparent = String(
          textBoxDefaultStyle.isBackgroundColorTransparent
        );

        if (textBoxDefaultStyle.isBackgroundColorTransparent) {
          textarea.style.backgroundColor = hexToRgba(
            textBoxDefaultStyle.textBoxFillColor,
            textBoxDefaultStyle.textBoxOpacity
          );
        } else {
          textarea.dataset.backgroundColor =
            textBoxDefaultStyle.textBoxFillColor;
          textarea.style.backgroundColor = "rgba(0, 0, 0, 0)";
        }
        textarea.style.border = "solid";
        textarea.style.borderWidth =
          String(textBoxDefaultStyle.textBoxBorderWidth) + "px";
        textarea.style.borderColor = textBoxDefaultStyle.textBoxBorderColor;
        textarea.dataset.opacity = String(textBoxDefaultStyle.textBoxOpacity);
        // textarea.style.opacity =
        //   String(textBoxDefaultStyle.textBoxOpacity) + "%";
        textarea.style.opacity = String(textBoxDefaultStyle.textBoxOpacity);
        // textarea.style.textShadow = `${textBoxDefaultStyle.textBoxHaloEffect_x}px ${textBoxDefaultStyle.textBoxHaloEffect_y}px ${textBoxDefaultStyle.textBoxHaloEffect_b}px ${textBoxDefaultStyle.textBoxHaloEffect},
        //   ${textBoxDefaultStyle.textBoxHaloEffect_x}px ${textBoxDefaultStyle.textBoxHaloEffect_y}px ${textBoxDefaultStyle.textBoxHaloEffect_b}px ${textBoxDefaultStyle.textBoxHaloEffect},
        //   ${textBoxDefaultStyle.textBoxHaloEffect_x}px ${textBoxDefaultStyle.textBoxHaloEffect_y}px ${textBoxDefaultStyle.textBoxHaloEffect_b}px ${textBoxDefaultStyle.textBoxHaloEffect},
        //   ${textBoxDefaultStyle.textBoxHaloEffect_x}px ${textBoxDefaultStyle.textBoxHaloEffect_y}px ${textBoxDefaultStyle.textBoxHaloEffect_b}px ${textBoxDefaultStyle.textBoxHaloEffect},
        //   ${textBoxDefaultStyle.textBoxHaloEffect_x}px ${textBoxDefaultStyle.textBoxHaloEffect_y}px ${textBoxDefaultStyle.textBoxHaloEffect_b}px ${textBoxDefaultStyle.textBoxHaloEffect},
        //   ${textBoxDefaultStyle.textBoxDropShadow_x}px ${textBoxDefaultStyle.textBoxDropShadow_y}px ${textBoxDefaultStyle.textBoxDropShadow_b}px ${textBoxDefaultStyle.textBoxDropShadow}`;
        textarea.dataset.currentSetting = "textSetting";
        textarea.dataset.leaderLineEnabled = "true";
      } else {
        textarea.value = defaultText;
        textarea.style.fontSize = "15px";
        textarea.style.color = "#000000";
        textarea.style.fontFamily = "Arial";
        textarea.style.textAlign = "left";
        textarea.style.resize = "none";
        textarea.dataset.isTransparent = String(
          "false"
          // textBoxDefaultStyle.isBackgroundColorTransparent
        );
        textarea.dataset.backgroundColor = textBoxDefaultStyle.textBoxFillColor;
        textarea.style.backgroundColor = "rgba(0, 0, 0, 0)";
        textarea.style.border = "solid";
        textarea.style.borderWidth =
          String(textBoxDefaultStyle.textBoxBorderWidth) + "px";
        textarea.style.borderColor = transparentColor;
        textarea.dataset.borderColor = textBoxDefaultStyle.textBoxBorderColor;
        textarea.dataset.opacity = String(textBoxDefaultStyle.textBoxOpacity);
        textarea.style.opacity = String(textBoxDefaultStyle.textBoxOpacity);
        textarea.dataset.textShadow = "";
        textarea.dataset.dropShadow = "";
        textarea.dataset.rotate = "0";
        // textarea.style.textShadow = `${textBoxDefaultStyle.textBoxHaloEffect_x}px ${textBoxDefaultStyle.textBoxHaloEffect_y}px ${textBoxDefaultStyle.textBoxHaloEffect_b}px ${transparentColor},
        //   ${textBoxDefaultStyle.textBoxHaloEffect_x}px ${textBoxDefaultStyle.textBoxHaloEffect_y}px ${textBoxDefaultStyle.textBoxHaloEffect_b}px ${transparentColor},
        //   ${textBoxDefaultStyle.textBoxHaloEffect_x}px ${textBoxDefaultStyle.textBoxHaloEffect_y}px ${textBoxDefaultStyle.textBoxHaloEffect_b}px ${transparentColor},
        //   ${textBoxDefaultStyle.textBoxHaloEffect_x}px ${textBoxDefaultStyle.textBoxHaloEffect_y}px ${textBoxDefaultStyle.textBoxHaloEffect_b}px ${transparentColor},
        //   ${textBoxDefaultStyle.textBoxHaloEffect_x}px ${textBoxDefaultStyle.textBoxHaloEffect_y}px ${textBoxDefaultStyle.textBoxHaloEffect_b}px ${transparentColor},
        //   ${textBoxDefaultStyle.textBoxDropShadow_x}px ${textBoxDefaultStyle.textBoxDropShadow_y}px ${textBoxDefaultStyle.textBoxDropShadow_b}px ${transparentColor}`;
        textarea.style.marginLeft = "-35px";
        textarea.style.height = "30px";
        textarea.style.width = "100px";
        textarea.dataset.currentSetting = "ringRadius";
        textarea.dataset.leaderLineEnabled = "false";
      }
    } else {
      textarea = previousValue;
      inputMarkerId = markerId;
    }

    // Please uncomment the code when you are using the drag icon for dragging the logo.

    outerDiv.appendChild(textarea);
    if (textarea && inputMarkerId) {
      // Create a draggable marker
      const textboxMarker = new mapboxgl.Marker(outerDiv, {
        draggable: true,
        className: "marker-remove-default-size textimage-icon-sec",
        anchor: "top-left",
      }).setLngLat([textarea.dataset.lng, textarea.dataset.lat]);
      const duplicateMarker = markerRef.current.find(
        (data) => data.id === inputMarkerId
      );
      if (!duplicateMarker) {
        markerRef.current.push({
          id: inputMarkerId,
          marker: textboxMarker,
          type: "textarea",
        });
        textboxMarker.addTo(map);
      } else {
        duplicateMarker.marker.setLngLat([
          textarea.dataset.lng,
          textarea.dataset.lat,
        ]);
        const textareaData = document.getElementById(inputMarkerId);
        if (textareaData) {
          textareaData.value = defaultText;
        }
      }

      outerDiv.addEventListener("click", function (event) {
        textarea.focus();
      });

      //Create the leader line connected to textbox
      mapDraw.changeMode("draw_line_string");
      const lineStringCoordinates = [
        [coordinates.lng, coordinates.lat],
        [coordinates.lng + 0.00001, coordinates.lat + +0.00001],
      ];

      const feature = {
        id: inputMarkerId,
        type: "Feature",
        properties: {
          "line-width": textBoxDefaultStyle.leaderLineWidth,
          "line-color": textBoxDefaultStyle.leaderLineColor,
        },
        geometry: {
          type: "LineString",
          coordinates: isCreate
            ? lineStringCoordinates
            : JSON.parse(previousValue.dataset.leaderLine).geometry[
                "coordinates"
              ],
        },
      };
      currentMode.current = "direct_select";
      const featureIds = mapDraw.add(feature);
      const lineConnected = mapDraw.get(featureIds[0]);
      textarea.dataset.leaderLine = JSON.stringify(feature);
      let updatedCoordinate;

      function onDocumentMouseUp() {
        map.setLayoutProperty(
          featureIds[0] + "-end-point",
          "visibility",
          "visible"
        );
        document.removeEventListener("mouseup", onDocumentMouseUp);
      }

      map.on("mousedown", featureIds[0] + "-end-point", function () {
        map.setLayoutProperty(
          featureIds[0] + "-end-point",
          "visibility",
          "none"
        );
        document.addEventListener("mouseup", onDocumentMouseUp);
      });

      map.on("draw.update", (e) => {
        const features = e.features;
        features.forEach((feature) => {
          if (
            feature.geometry.type === "LineString" &&
            featureIds[0] === feature.id
          ) {
            const coordinates = feature.geometry.coordinates;
            updatedCoordinate = coordinates[0];
            map.getSource(featureIds[0] + "-end-point").setData({
              type: "FeatureCollection",
              features: [
                {
                  type: "Feature",
                  geometry: {
                    type: "Point",
                    coordinates: updatedCoordinate,
                  },
                },
              ],
            });
          }
        });
      });

      lineConnected.properties["line-color"] =
        textBoxDefaultStyle.leaderLineColor;
      lineConnected.properties["line-width"] =
        textBoxDefaultStyle.leaderLineWidth;
      lineConnected.properties["vertexColor"] =
        textBoxDefaultStyle.leaderLineColor;
      currentMode.current = "direct_select";
      mapDraw.setFeatureProperty(
        featureIds[0],
        "line-color",
        textBoxDefaultStyle.leaderLineColor
      );
      mapDraw.setFeatureProperty(
        featureIds[0],
        "vertexColor",
        textBoxDefaultStyle.leaderLineColor
      );

      mapDraw.add(lineConnected);
      CommonUtils.UpdateVertexColorLayer({
        featureId: featureIds[0],
        mapDraw,
        map,
        currentMode,
        polygonDefaultStyle,
      });
      textarea.dataset.lineConnected = featureIds[0];
      setTextBox((previousValue) => {
        previousValue.textBoxInstance[inputMarkerId] = {
          lineConnected: featureIds[0],
          textboxMarker,
          outerDiv,
        };
        return previousValue;
      });

      // updateAutoSave();
      if (isEnabledAction === "textSetting") {
        setTimeout(() => {
          if (isCreate) {
            this.GetStyleFromTextBox({
              inputMarkerId,
              connectedLeaderLine: featureIds[0],
              setTextBox,
              mapDraw,
            });
          }
        }, 100);
      }
      // Event listener to show the button on right-click
      outerDiv.addEventListener("contextmenu", (event) => {
        if (disableUserActions()) {
          return;
        }
        lastClickButton.current = 0;
        currentMode.current = "direct_select";
        resetAllOnceChanged();
        this.GetStyleFromTextBox({
          inputMarkerId,
          connectedLeaderLine: featureIds[0],
          setTextBox,
          mapDraw,
        });
        event.preventDefault(); // Prevent the default context menu from showing
      });
      // Event listener for drag end
      textboxMarker.on("dragend", (event) => {
        if (disableUserActions()) {
          return;
        }
        this.OnMoveTextBox({
          coordinates: {
            lng: event.target._lngLat.lng,
            lat: event.target._lngLat.lat,
          },
          connectedLeaderLine: featureIds[0],
          mapDraw,
          textAreaElement: textarea,
          map,
          textBoxDefaultStyle,
        });
        textarea.dataset.lat = event.target._lngLat.lat;
        textarea.dataset.lng = event.target._lngLat.lng;
        this.updatePreviousValueChange(
          {
            action: "updated",
            setDrawnFeaturesSupport,
            inputMarkerId,
            textarea,
            setTextBox,
          }
          // setUndoHistory,
          // setRedoHistory
        );
        // textboxMarker.setDraggable(false);
        // updateAutoSave();
      });
      // Event listener for drag event to update the polyline dynamically
      textboxMarker.on("drag", (event) => {
        if (disableUserActions()) {
          return;
        }
        this.OnMoveTextBox({
          coordinates: {
            lng: event.target._lngLat.lng,
            lat: event.target._lngLat.lat,
          },
          connectedLeaderLine: featureIds[0],
          mapDraw,
          textAreaElement: textarea,
          map,
          textBoxDefaultStyle,
        });
      });

      // Use interactjs to make the textarea resizable
      interact(textarea).resizable({
        edges: { left: false, right: true, bottom: true, top: false },
        listeners: {
          onstart() {
            map.dragPan.disable();
          },
          move(event) {
            map.dragPan.disable();
            if (disableUserActions()) {
              return;
            }
            var target = event.target;
            var x = parseFloat(target.getAttribute("data-x")) || 0;
            var y = parseFloat(target.getAttribute("data-y")) || 0;

            target.style.width = event.rect.width + "px";
            target.style.height = event.rect.height + "px";

            x += event.deltaRect.left;
            y += event.deltaRect.top;

            target.style.transform = "translate(" + x + "px," + y + "px)";

            target.setAttribute("data-x", x);
            target.setAttribute("data-y", y);
          },
          end() {
            setTextBox((previousValue) => {
              if (previousValue && previousValue.selectedTextBox) {
                previousValue.selectedTextBox.style.textBox.width = parseInt(
                  textarea.style.width
                );
                previousValue.selectedTextBox.style.textBox.height = parseInt(
                  textarea.style.height
                );
                previousValue.selectedTextBox.isResetStyle = true;
              }
              return previousValue;
            });
            CustomTextBoxClass.updatePreviousValueChange(
              {
                action: "updated",
                setDrawnFeaturesSupport,
                inputMarkerId,
                textarea,
                setTextBox,
              }
              // setUndoHistory,
              // setRedoHistory
            );
            map.dragPan.enable();
            textboxMarker.setDraggable(disableUserActions() ? false : true);
          },
        },
        modifiers: [
          interact.modifiers.restrictEdges({
            outer: "parent",
            endOnly: true,
            enabled: false,
          }),
        ],
        inertia: true,
      });
      if (isCreate) {
        this.updatePreviousValueChange({
          action: "created",
          setDrawnFeaturesSupport,
          inputMarkerId,
          textarea,
          setTextBox,
          // setUndoHistory,
          // setRedoHistory,
          isEnabledAction,
        });
      } else {
        if (JSON.parse(textarea.dataset.leaderLineEnabled)) {
          const lineConnected = JSON.parse(textarea.dataset.leaderLine);
          const source = map.getSource(
            textarea.dataset.lineConnected + "-end-point"
          );
          if (!source) {
            const icon = {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: lineConnected.geometry.coordinates[0],
              },
            };
            map.addSource(textarea.dataset.lineConnected + "-end-point", {
              type: "geojson",
              data: icon,
            });
            map.addLayer({
              id: textarea.dataset.lineConnected + "-end-point",
              type: "circle",
              source: textarea.dataset.lineConnected + "-end-point",
              paint: {
                "circle-radius": 6,
                "circle-color": lineConnected.properties["line-color"],
              },
            });
          }
          mapDraw.setFeatureProperty(
            textarea.dataset.lineConnected,
            "coordinates",
            lineConnected.geometry.coordinates
          );
          mapDraw.add(lineConnected);
        } else {
          CommonUtils.HideOrShowLeaderLine({
            mapDraw,
            connectedLeaderLine: textarea.dataset.lineConnected,
            correspondingElement: textarea,
            leaderLine: {
              leaderLineColor: textBoxDefaultStyle.leaderLineColor,
              leaderLineWidth: textBoxDefaultStyle.leaderLineWidth,
            },
            condition: true,
            map,
          });
        }
      }

      // leader line hide on from radiusSetting
      if (isEnabledAction === "radiusSetting") {
        CommonUtils.HideOrShowLeaderLine({
          mapDraw,
          connectedLeaderLine: textarea.dataset.lineConnected,
          correspondingElement: textarea,
          leaderLine: {
            leaderLineColor: textBoxDefaultStyle.leaderLineColor,
            leaderLineWidth: textBoxDefaultStyle.leaderLineWidth,
          },
          condition: true,
          map,
        });
      }

      // Check for mouse event for textarea
      textarea.addEventListener("mousedown", function (event) {
        const textareaRect = textarea.getBoundingClientRect();
        const offsetX = event.clientX - textareaRect.right;
        const offsetY = event.clientY - textareaRect.bottom;
        const threshold = 20; // Adjust this value as needed
        const isRightEdge = Math.abs(offsetX) <= threshold;
        const isBottomEdge = Math.abs(offsetY) <= threshold;

        if (isRightEdge || isBottomEdge) {
          textboxMarker.setDraggable(false);
          // setTimeout(() => {
          // map.dragPan.disable();
          // }, 10);
        }
      });

      textarea.addEventListener("mouseleave", function (event) {
        if (disableUserActions()) {
          return;
        }
        textboxMarker.setDraggable(disableUserActions() ? false : true);
        map.dragPan.enable();
      });

      textarea.addEventListener("dblclick", function (event) {
        map.doubleClickZoom.disable();
        textarea.select();
      });

      textarea.addEventListener("keydown", function (event) {
        const disallowedKeys = [
          "+",
          "-",
          "ArrowLeft",
          "ArrowRight",
          "ArrowDown",
          "ArrowUp",
        ];

        if (
          disallowedKeys.includes(event.key) ||
          (event.shiftKey && disallowedKeys.slice(2).includes(event.key))
        ) {
          event.stopPropagation();
        }
      });
      textarea.addEventListener("mousemove", function (event) {
        const currentState = textarea.style.cursor;
        const resizeStates = ["text", "ns-resize", "nwse-resize", "ew-resize"];
        textarea.style.cursor = resizeStates.includes(currentState)
          ? currentState
          : "pointer";
      });
      textarea.addEventListener("click", function (event) {
        textarea.focus();
        textarea.style.cursor = "text";
        const endPosition = textarea.value.length;
        textarea.setSelectionRange(endPosition, endPosition);
      });
      textarea.addEventListener("input", (e) => {
        setTextBox((previousValue) => {
          if (previousValue && previousValue.selectedTextBox) {
            previousValue.selectedTextBox.style.textBox.content =
              textarea.value;
            previousValue.selectedTextBox.isResetStyle = true;
          }
          return previousValue;
        });
        this.updatePreviousValueChange(
          {
            action: "updated",
            setDrawnFeaturesSupport,
            inputMarkerId,
            textarea,
            setTextBox,
          }
          // setUndoHistory,
          // setRedoHistory
        );
      });

      mapDraw.changeMode("simple_select");
      map.getCanvas().style.cursor = "default";
      if (isCreate) {
        setActiveId("none");
      }
    }
  };

  static OnStyleChangeTextBox = ({
    inputMarkerId,
    connectedLeaderLine,
    changedStyle,
    setTextBox,
    mapDraw,
    textBoxDefaultStyle,
    currentMode,
    map,
    polygonDefaultStyle,
    // setUndoHistory,
    // setRedoHistory,
    setDrawnFeaturesSupport,
    isUpdate,
    resetAllOnceChanged,
    markers,
  }) => {
    const textarea = document.getElementById(inputMarkerId);
    if (textarea) {
      textarea.value = changedStyle.textBox.content;
    }
    const lineConnected = mapDraw.get(connectedLeaderLine);
    if (changedStyle.leaderLine.isLineTransparent) {
      textarea.dataset.leaderLineEnabled = "true";
    } else {
      textarea.dataset.leaderLineEnabled = "false";
    }
    if (changedStyle.font.size >= 15 && changedStyle.font.size <= 90)
      textarea.style.fontSize = String(changedStyle.font.size) + "px";
    textarea.style.fontStyle =
      changedStyle.font.style &&
      ["italic", "boldItalic"].includes(changedStyle.font.style)
        ? "italic"
        : "";
    textarea.style.fontWeight =
      changedStyle.font.style &&
      ["bold", "boldItalic"].includes(changedStyle.font.style)
        ? "bold"
        : "";
    textarea.style.color = changedStyle.font.color;
    textarea.style.fontFamily = changedStyle.font.fontFamily;
    textarea.style.textAlign = changedStyle.font.alignment;
    textarea.dataset.opacity = String(changedStyle.textBox.opacity);
    textarea.style.width = String(changedStyle.textBox.width) + "px";
    textarea.style.height = String(changedStyle.textBox.height) + "px";
    textarea.dataset.isTransparent = String(
      changedStyle.textBox.isFillTransparent
    );
    textarea.dataset.rotate = changedStyle.textBox.rotate;
    textarea.style.transform = `rotate(${changedStyle.textBox.rotate}deg)`;

    if (changedStyle.textBox.isFillTransparent) {
      textarea.style.backgroundColor = markers
        ? changedStyle.textBox.fillColor.startsWith("rgba(")
          ? (() => {
              try {
                return Rgba2hex(changedStyle.textBox.fillColor, true);
              } catch (error) {
                return changedStyle.textBox.fillColor;
              }
            })()
          : hexToRgba(
              changedStyle.textBox.fillColor,
              changedStyle.textBox.opacity
            )
        : changedStyle.textBox.fillColor;
      textarea.dataset.backgroundColor =
        changedStyle.textBox.fillColor.startsWith("rgba(")
          ? (() => {
              try {
                return Rgba2hex(changedStyle.textBox.fillColor, true);
              } catch (error) {
                return changedStyle.textBox.fillColor;
              }
            })()
          : changedStyle.textBox.fillColor;
    } else {
      if (textarea.style.backgroundColor === transparentColor) {
        changedStyle.textBox.dropShadow = textarea.dataset.backgroundColor;
      }
      textarea.style.backgroundColor = transparentColor;
    }
    if (!changedStyle.textBox.isBorderTransparent) {
      textarea.style.borderColor = transparentColor;
    } else {
      if (textarea.style.borderColor === transparentColor) {
        textarea.style.borderColor = textarea.dataset.boxShadow;
        changedStyle.textBox.borderColor = textarea.dataset.borderColor;
      } else {
        textarea.style.borderColor = changedStyle.textBox.borderColor;
        textarea.dataset.borderColor = changedStyle.textBox.borderColor;
      }
    }
    if (!isUpdate) {
      textarea.dataset.lat = changedStyle.textBox.lat;
      textarea.dataset.lng = changedStyle.textBox.lng;
    }
    if (changedStyle.textBox.borderWidth > 0) {
      textarea.style.borderWidth =
        String(changedStyle.textBox.borderWidth) + "px";
    }
    const {
      haloEffect_x,
      haloEffect_y,
      haloEffect_b,
      dropShadow_x,
      dropShadow_y,
      dropShadow_b,
      isHaloEffectTransparent,
      isDropShadowTransparent,
    } = changedStyle.textBox;

    const generateTextShadow = (haloEffect, dropShadow) => `
        ${haloEffect_x}px ${haloEffect_y}px ${haloEffect_b}px ${haloEffect},
        ${haloEffect_x}px ${haloEffect_y}px ${haloEffect_b * 2}px ${haloEffect},
        ${haloEffect_x}px ${haloEffect_y}px ${haloEffect_b * 3}px ${haloEffect},
        ${haloEffect_x}px ${haloEffect_y}px ${haloEffect_b * 4}px ${haloEffect},
        ${haloEffect_x}px ${haloEffect_y}px ${haloEffect_b * 5}px ${haloEffect},
        ${dropShadow_x}px ${dropShadow_y}px ${dropShadow_b}px ${dropShadow}
    `;

    if (!isHaloEffectTransparent && isDropShadowTransparent) {
      textarea.style.textShadow = generateTextShadow(
        transparentColor,
        changedStyle.textBox.dropShadow
      );
      textarea.dataset.dropShadow = changedStyle.textBox.dropShadow;
    } else if (isHaloEffectTransparent && !isDropShadowTransparent) {
      textarea.style.textShadow = generateTextShadow(
        changedStyle.textBox.haloEffect,
        transparentColor
      );
      textarea.dataset.textShadow = changedStyle.textBox.haloEffect;
    } else if (!isHaloEffectTransparent && !isDropShadowTransparent) {
      textarea.style.textShadow = generateTextShadow(
        transparentColor,
        transparentColor
      );
    } else {
      textarea.style.textShadow = generateTextShadow(
        changedStyle.textBox.haloEffect,
        changedStyle.textBox.dropShadow
      );
      textarea.dataset.textShadow = changedStyle.textBox.haloEffect;
      textarea.dataset.dropShadow = changedStyle.textBox.dropShadow;
    }

    const parserLeaderLine = JSON.parse(textarea.dataset.leaderLine);
    parserLeaderLine["properties"]["line-color"] =
      changedStyle.leaderLine.color;
    parserLeaderLine["properties"]["line-width"] =
      changedStyle.leaderLine.width;
    textarea.dataset.leaderLine = JSON.stringify(parserLeaderLine);
    const feature = CommonUtils.HideOrShowLeaderLine({
      mapDraw,
      connectedLeaderLine,
      correspondingElement: textarea,
      leaderLine: {
        leaderLineColor: textBoxDefaultStyle.leaderLineColor,
        leaderLineWidth: textBoxDefaultStyle.leaderLineWidth,
      },
      condition: !changedStyle.leaderLine.isLineTransparent,
      map,
    });
    if (feature) {
      currentMode.current = "direct_select";
      textarea.dataset.connectedLeaderLineColor = changedStyle.leaderLine.color;
      feature.properties["line-color"] = changedStyle.leaderLine.color;
      if (changedStyle.leaderLine.width > 0) {
        textarea.dataset.connectedLeaderLineWidth = String(
          changedStyle.leaderLine.width
        );
        feature.properties["line-width"] = changedStyle.leaderLine.width * 1;
      }
      feature.properties.vertexColor = changedStyle.leaderLine.color;
      const vertexPoint = map.getLayer(feature.id + "-end-point");
      if (vertexPoint) {
        map.setPaintProperty(
          vertexPoint.id,
          "circle-color",
          changedStyle.leaderLine.color
        );
      }

      if (changedStyle.leaderLine.isLineTransparent) {
        mapDraw.add(feature);
        if (changedStyle.leaderLine.width > 0) {
          mapDraw.setFeatureProperty(
            connectedLeaderLine,
            "line-width",
            changedStyle.leaderLine.width * 1
          );
        }
        mapDraw.setFeatureProperty(
          connectedLeaderLine,
          "line-color",
          changedStyle.leaderLine.color
        );
        CommonUtils.UpdateVertexColorLayer({
          featureId: connectedLeaderLine,
          mapDraw,
          map,
          currentMode,
          polygonDefaultStyle,
        });
      }
    }
    setTextBox((previousValue) => {
      const selectedTextBox = {
        style: {
          font: {
            color: changedStyle.font.color,
            fontFamily: changedStyle.font.fontFamily,
            size: changedStyle.font.size,
            style: changedStyle.font.style,
            alignment: changedStyle.font.alignment,
          },
          textBox: {
            fillColor: changedStyle.textBox.fillColor,
            isFillTransparent: changedStyle.textBox.isFillTransparent,
            borderColor: changedStyle.textBox.borderColor,
            borderWidth: changedStyle.textBox.borderWidth,
            isBorderTransparent: changedStyle.textBox.isBorderTransparent,
            opacity: changedStyle.textBox.opacity,
            haloEffect: changedStyle.textBox.haloEffect,
            haloEffect_x: changedStyle.textBox.haloEffect_x,
            haloEffect_y: changedStyle.textBox.haloEffect_y,
            haloEffect_b: changedStyle.textBox.haloEffect_b,
            haloEffect_c: changedStyle.textBox.haloEffect_c,
            isHaloEffectTransparent:
              changedStyle.textBox.isHaloEffectTransparent,
            dropShadow: changedStyle.textBox.dropShadow,
            dropShadow_x: changedStyle.textBox.dropShadow_x,
            dropShadow_y: changedStyle.textBox.dropShadow_y,
            dropShadow_b: changedStyle.textBox.dropShadow_b,
            dropShadow_c: changedStyle.textBox.dropShadow_c,
            isDropShadowTransparent:
              changedStyle.textBox.isDropShadowTransparent,
            currentSetting: changedStyle.textBox.currentSetting,
            width: changedStyle.textBox.width,
            height: changedStyle.textBox.height,
            content: changedStyle.textBox.content,
            rotate: changedStyle.textBox.rotate,
            lat: changedStyle.textBox.lat,
            lng: changedStyle.textBox.lng,
          },
          leaderLine: {
            color: changedStyle.leaderLine.color,
            width: changedStyle.leaderLine.width,
            isLineTransparent: changedStyle.leaderLine.isLineTransparent,
            coordinates: changedStyle.leaderLine.coordinates,
          },
        },
        id: inputMarkerId,
        leaderLine: connectedLeaderLine,
        isResetStyle: !isUpdate,
      };
      return { ...previousValue, selectedTextBox: selectedTextBox };
    });

    if (markers) {
      const selectedMarker = markers.filter(
        (item) => item.id === inputMarkerId
      )[0];
      selectedMarker.marker.setLngLat([
        +changedStyle.textBox.lng,
        +changedStyle.textBox.lat,
      ]);
      this.OnMoveTextBox({
        coordinates: {
          lng: +changedStyle.textBox.lng,
          lat: +changedStyle.textBox.lat,
        },
        connectedLeaderLine,
        mapDraw,
        textAreaElement: textarea,
        map,
        textBoxDefaultStyle,
        isUpdate: true,
        allCoordinates: changedStyle.leaderLine.coordinates,
      });
      if (changedStyle.leaderLine.isLineTransparent) {
        const parsedLeaderLine = JSON.parse(textarea.dataset.leaderLine);
        const featureIds = mapDraw.add(parsedLeaderLine);
        let updatedCoordinate;
        if (parsedLeaderLine.geometry.type === "LineString") {
          const coordinates = parsedLeaderLine.geometry.coordinates;
          updatedCoordinate = coordinates[0];
          map.getSource(featureIds[0] + "-end-point")?.setData({
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: updatedCoordinate,
                },
              },
            ],
          });
        }
      }
    }
    if (isUpdate) {
      this.updatePreviousValueChange({
        action: "updated",
        setDrawnFeaturesSupport,
        inputMarkerId,
        textarea,
        setTextBox,
        // setUndoHistory,
        // setRedoHistory,
      });
    } else {
      resetAllOnceChanged();
      textarea.focus();
    }
  };

  static DeleteTextBox = ({
    inputMarkerId,
    mapDraw,
    setTextBox,
    // setUndoHistory,
    // setRedoHistory,
    setDrawnFeaturesSupport,
    isDelete,
    map,
    markerRef,
  }) => {
    if (inputMarkerId) {
      setTextBox((previousValue) => {
        const textBoxInstance = {
          ...previousValue.textBoxInstance[inputMarkerId],
        };
        if (map) {
          const vertexPoint = map.getLayer(
            textBoxInstance.lineConnected + "-end-point"
          );
          if (vertexPoint) {
            map.removeLayer(textBoxInstance.lineConnected + "-end-point");
            map.removeSource(textBoxInstance.lineConnected + "-end-point");
          }
        }
        textBoxInstance.textboxMarker?.remove();
        mapDraw.delete(textBoxInstance.lineConnected);
        previousValue.selectedTextBox = null;
        return previousValue;
      });
      CommonUtils.RemoveMarkerFromList({ markerRef, id: inputMarkerId });
      if (isDelete) {
        this.updatePreviousValueChange({
          action: "deleted",
          setDrawnFeaturesSupport,
          inputMarkerId,
          textarea: "",
          setTextBox,
          // setUndoHistory,
          // setRedoHistory
        });
      }
    }
  };

  static RecoverTextBox = ({
    coordinates,
    mapboxgl,
    map,
    mapDraw,
    textareaElement,
    resetAllOnceChanged,
    setTextBox,
    lastClickButton,
    updateAutoSave,
    textBoxDefaultStyle,
    setDrawnFeaturesSupport,
    setUndoHistory,
    setRedoHistory,
    disableUserActions,
    markerRef,
  }) => {
    // Create the outer div element
    const outerDiv = document.createElement("div");
    const inputMarkerId = textareaElement.id;
    textareaElement.style.opacity = "";

    // Please uncomment the code when you are using the drag icon for dragging the logo.
    // const textBoxButtons = GetTextBoxInteractionButtons();
    // outerDiv.appendChild(textBoxButtons.move);
    // outerDiv.appendChild(textBoxButtons.hide);
    outerDiv.appendChild(textareaElement);

    // Create a draggable marker
    const textboxMarker = new mapboxgl.Marker(outerDiv, {
      draggable: true,
      className: "marker-remove-default-size",
      anchor: "top-left",
    })
      .setLngLat([coordinates.lng, coordinates.lat])
      .addTo(map);
    outerDiv.addEventListener("click", function (event) {
      textareaElement.focus();
    });
    setDrawnFeaturesSupport((previousValue) => {
      previousValue[inputMarkerId] = textareaElement.cloneNode();
      return previousValue;
    });
    setTextBox((previousValue) => {
      previousValue.textBoxInstance[inputMarkerId] = {
        lineConnected: textareaElement.dataset.lineConnected,
        textboxMarker,
        outerDiv,
      };
      return previousValue;
    });
    markerRef.current.push({
      id: inputMarkerId,
      marker: textboxMarker,
      type: "textarea",
    });
    updateAutoSave();
    // Event listener to show the button on right-click
    outerDiv.addEventListener("contextmenu", (event) => {
      if (disableUserActions()) return;
      lastClickButton.current = 0;
      resetAllOnceChanged();
      this.GetStyleFromTextBox({
        inputMarkerId,
        connectedLeaderLine: textareaElement.dataset.lineConnected,
        setTextBox,
        mapDraw,
        textBoxDefaultStyle,
      });
      event.preventDefault(); // Prevent the default context menu from showing
    });

    let updatedCoordinate;
    map.on("draw.update", (e) => {
      const features = e.features;
      features.forEach((feature) => {
        if (feature.geometry.type === "LineString") {
          function onDocumentMouseUp() {
            map.setLayoutProperty(
              feature.id + "-end-point",
              "visibility",
              "visible"
            );
            document.removeEventListener("mouseup", onDocumentMouseUp);
          }
          map.on("mousedown", feature.id + "-end-point", function () {
            map.setLayoutProperty(
              feature.id + "-end-point",
              "visibility",
              "none"
            );
            document.addEventListener("mouseup", onDocumentMouseUp);
          });
          const coordinates = feature.geometry.coordinates;
          updatedCoordinate = coordinates[0];
          map.getSource(feature.id + "-end-point").setData({
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: updatedCoordinate,
                },
              },
            ],
          });
        }
      });
    });

    // Event listener for drag end
    textboxMarker.on("dragend", (event) => {
      if (disableUserActions()) return;
      this.OnMoveTextBox({
        coordinates: {
          lng: event.target._lngLat.lng,
          lat: event.target._lngLat.lat,
        },
        connectedLeaderLine: textareaElement.dataset.lineConnected,
        mapDraw,
        textAreaElement: textareaElement,
        map,
        textBoxDefaultStyle,
      });
      textareaElement.dataset.lat = event.target._lngLat.lat;
      textareaElement.dataset.lng = event.target._lngLat.lng;
      this.updatePreviousValueChange({
        action: "updated",
        setDrawnFeaturesSupport,
        inputMarkerId,
        textarea: textareaElement,
        setTextBox,
        setUndoHistory,
        setRedoHistory,
      });
      //textboxMarker.setDraggable(false);
      updateAutoSave();
    });
    // Event listener for drag event to update the polyline dynamically
    textboxMarker.on("drag", (event) => {
      if (disableUserActions()) return;
      this.OnMoveTextBox({
        coordinates: {
          lng: event.target._lngLat.lng,
          lat: event.target._lngLat.lat,
        },
        connectedLeaderLine: textareaElement.dataset.lineConnected,
        mapDraw,
        textAreaElement: textareaElement,
        map,
        textBoxDefaultStyle,
      });
    });

    // Use interactjs to make the textarea resizable
    interact(textareaElement).resizable({
      edges: { left: false, right: true, bottom: true, top: false },

      listeners: {
        move(event) {
          if (disableUserActions()) return;
          var target = event.target;
          var x = parseFloat(target.getAttribute("data-x")) || 0;
          var y = parseFloat(target.getAttribute("data-y")) || 0;

          target.style.width = event.rect.width + "px";
          target.style.height = event.rect.height + "px";

          x += event.deltaRect.left;
          y += event.deltaRect.top;

          target.style.transform = "translate(" + x + "px," + y + "px)";

          target.setAttribute("data-x", x);
          target.setAttribute("data-y", y);
        },
        end() {
          setTextBox((previousValue) => {
            if (previousValue && previousValue.selectedTextBox) {
              previousValue.selectedTextBox.style.textBox.width = parseInt(
                textareaElement.style.width
              );
              previousValue.selectedTextBox.style.textBox.height = parseInt(
                textareaElement.style.height
              );
              previousValue.selectedTextBox.isResetStyle = true;
            }
            return previousValue;
          });
          CustomTextBoxClass.updatePreviousValueChange({
            action: "updated",
            setDrawnFeaturesSupport,
            inputMarkerId,
            textarea: textareaElement,
            setTextBox,
            setUndoHistory,
            setRedoHistory,
          });
          map.dragPan.enable();
          textboxMarker.setDraggable(disableUserActions() ? false : true);
        },
      },
      modifiers: [
        interact.modifiers.restrictEdges({
          outer: "parent",
          endOnly: true,
          enabled: false,
        }),
      ],
      inertia: true,
    });

    // Check for mouse event for textarea
    textareaElement.addEventListener("mousedown", function (event) {
      const textareaRect = textareaElement.getBoundingClientRect();
      const offsetX = event.clientX - textareaRect.right;
      const offsetY = event.clientY - textareaRect.bottom;
      const threshold = 20; // Adjust this value as needed
      const isRightEdge = Math.abs(offsetX) <= threshold;
      const isBottomEdge = Math.abs(offsetY) <= threshold;

      if (isRightEdge || isBottomEdge) {
        textboxMarker.setDraggable(false);
        map.dragPan.disable();
      }
    });

    textareaElement.addEventListener("mouseleave", function (event) {
      if (disableUserActions()) return;
      textboxMarker.setDraggable(disableUserActions() ? false : true);
      map.dragPan.enable();
    });

    textareaElement.addEventListener("dblclick", function (event) {
      map.doubleClickZoom.disable();
      textareaElement.select();
    });

    textareaElement.addEventListener("keydown", function (event) {
      const disallowedKeys = [
        "+",
        "-",
        "ArrowLeft",
        "ArrowRight",
        "ArrowDown",
        "ArrowUp",
      ];

      if (
        disallowedKeys.includes(event.key) ||
        (event.shiftKey && disallowedKeys.slice(2).includes(event.key))
      ) {
        event.stopPropagation();
      }
    });

    textareaElement.addEventListener("click", function (event) {
      const { offsetX, offsetY } = event;
      const rect = textareaElement.getBoundingClientRect();
      const textAreaX = offsetX + rect.left;
      const textAreaY = offsetY + rect.top;

      // Create a hidden canvas to calculate the text position
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      if (context) {
        context.font = window.getComputedStyle(textareaElement).font;

        // Calculate the number of rows and columns
        const lineHeight = parseInt(
          window.getComputedStyle(textareaElement).lineHeight,
          10
        );
        const textAreaContent = textareaElement.value;

        let row = Math.floor((textAreaY - rect.top) / lineHeight);
        if (row >= textareaElement.rows) row = textareaElement.rows - 1;

        // Calculate column based on the width of the text
        const textWidth = context.measureText(textAreaContent).width;
        const averageCharWidth = textWidth / textAreaContent.length;
        let column = Math.floor((textAreaX - rect.left) / averageCharWidth);
        if (column >= textAreaContent.length)
          column = textAreaContent.length - 1;

        // Set the cursor position
        const index = Math.min(
          textAreaContent.length,
          row * textareaElement.cols + column
        );
        textareaElement.selectionStart = textareaElement.selectionEnd = index;
        textareaElement.focus();
      }
    });

    textareaElement.addEventListener("input", (e) => {
      setTextBox((previousValue) => {
        if (previousValue && previousValue.selectedTextBox) {
          previousValue.selectedTextBox.style.textBox.content =
            textareaElement.value;
          previousValue.selectedTextBox.isResetStyle = true;
        }
        return previousValue;
      });
      this.updatePreviousValueChange({
        action: "updated",
        setDrawnFeaturesSupport,
        inputMarkerId,
        textarea: textareaElement,
        setTextBox,
        setUndoHistory,
        setRedoHistory,
      });
    });
    // Please uncomment the code when you are using the drag icon for dragging the logo.
    // textBoxButtons.move.addEventListener("mousedown", function (event) {
    //     textboxMarker.setDraggable(true);
    // });
    // textBoxButtons.move.addEventListener("mouseup", function (event) {
    //     textboxMarker.setDraggable(false);
    // });
    // textBoxButtons.hide.addEventListener("mouseup", function (event) {
    //     HideOrShowLeaderLine(
    //         mapDraw,
    //         textareaElement.dataset.lineConnected,
    //         textareaElement,
    //         {
    //             leaderLineColor: textBoxDefaultStyle.leaderLineColor,
    //             leaderLineWidth: textBoxDefaultStyle.leaderLineWidth,
    //         },
    //         true
    //     );
    //     setTextBox((previousValue) => {
    //         if (previousValue && previousValue.selectedTextBox) {
    //             previousValue.selectedTextBox.style.leaderLine = {
    //                 color: textBoxDefaultStyle.leaderLineColor,
    //                 width: textBoxDefaultStyle.leaderLineWidth,
    //                 isLineTransparent: false,
    //             };
    //             previousValue.selectedTextBox.isResetStyle = true
    //         }
    //         return previousValue;
    //     });

    // });
  };
}
